import { Check, RotateCcw } from 'lucide-react';
import { Link, Navigate } from 'react-router';
import { Page } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader } from '@/components/ui/card';
import { useSetup, useSetupAction, useSetupApplies } from '@/hooks/useSetup';
import { cn } from '@/lib/utils';
import { LATER_STEPS, doneCount, laterDone, stepSource } from './steps';
import { useSetupSteps } from './useSetupSteps';

/**
 * `/setup`: the same steps as the rail, as a page, each with Skip. The sidebar
 * item's Open points here. Onboarding over
 * (every step done) or never theirs (joined by invite): straight to the app.
 */
export function SetupPage() {
  const applies = useSetupApplies();
  const setup = useSetup();
  const action = useSetupAction();
  const steps = useSetupSteps();
  const state = setup.data;

  if (!applies || state?.finished) return <Navigate to="/timesheet" replace />;

  if (setup.isPending || !state) {
    return (
      <Page title="Setup">
        <div className="skeleton h-64 w-full rounded-2xl" />
      </Page>
    );
  }

  const done = doneCount(steps, state);

  return (
    <Page
      title="Setup"
      description={`${done} of ${steps.length} done.`}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => action.mutate('startOver')}
            disabled={action.isPending}
          >
            <RotateCcw />
            Start over
          </Button>
          <Button
            variant={state.hidden ? 'primary' : 'secondary'}
            onClick={() => action.mutate(state.hidden ? 'show' : 'hide')}
            disabled={action.isPending}
          >
            {state.hidden ? 'Show setup guide' : 'Hide setup'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <Card>
          <CardHeader title="Steps" description="Each one ticks itself from your data." />
          <CardBody className="p-0">
            <ol className="divide-y divide-[var(--line)]">
              {steps.map((step, i) => {
                const isDone = step.isDone(state);
                const source = stepSource(state, step.id);
                return (
                  <li key={step.id} className="flex items-center gap-4 px-6 py-4">
                    <span
                      className={cn(
                        'tnum flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                        isDone
                          ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
                          : 'border border-[var(--line-strong)] text-muted'
                      )}
                    >
                      {isDone ? <Check className="size-3.5" /> : i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={cn('text-sm font-medium', isDone && 'text-muted line-through')}>
                        {step.title}
                        {isDone && source === 'imported' ? (
                          <span className="ml-2 text-xs font-normal no-underline text-subtle">
                            imported
                          </span>
                        ) : null}
                        {isDone && source === 'skipped' ? (
                          <span className="ml-2 text-xs font-normal text-subtle">skipped</span>
                        ) : null}
                      </p>
                      <p className="text-sm text-muted">{step.body}</p>
                    </div>
                    {!isDone ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => action.mutate({ action: 'skipStep', step: step.id })}
                        disabled={action.isPending}
                      >
                        Skip
                      </Button>
                    ) : null}
                    <Button variant={isDone ? 'ghost' : 'secondary'} size="sm" asChild>
                      <Link to={`${step.cta.to ?? step.route}?setup=1`}>
                        {isDone ? 'Open' : step.cta.label}
                      </Link>
                    </Button>
                  </li>
                );
              })}
            </ol>
          </CardBody>
        </Card>

        {state.isAdmin ? (
          <Card>
            <CardHeader title="When you're ready" description="Never blocking." />
            <CardBody className="p-0">
              <ul className="divide-y divide-[var(--line)]">
                {LATER_STEPS.map((later) => {
                  const isDone = laterDone(state, later.id);
                  return (
                    <li key={later.id} className="flex items-center gap-4 px-6 py-4">
                      <span
                        className={cn(
                          'flex size-7 shrink-0 items-center justify-center rounded-full',
                          isDone
                            ? 'bg-[var(--accent)] text-[var(--accent-fg)]'
                            : 'border border-dashed border-[var(--line-strong)]'
                        )}
                      >
                        {isDone ? <Check className="size-3.5" /> : null}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-sm font-medium', isDone && 'text-muted line-through')}>
                          {later.title}
                        </p>
                        <p className="text-sm text-muted">{later.body}</p>
                      </div>
                      <Button variant={isDone ? 'ghost' : 'secondary'} size="sm" asChild>
                        <Link to={later.to}>{isDone ? 'Open' : 'Set up'}</Link>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </CardBody>
          </Card>
        ) : null}
      </div>
    </Page>
  );
}
